<script setup lang="ts">
import { KsDrawer } from '@aschehoug/kloss'
import { faArrowRight } from '@fortawesome/pro-light-svg-icons'

defineProps<{
  contentTypeOptions: CheckboxOption[]
  originalTotalResults: number
  legalCategoryOptions: CheckboxOption[]
  authorOptions: CheckboxOption[]
  changeSortOrder: (value: { value: string, label: string }) => void
}>()

const filterState = useSearchFilters({ resetPageOnFilterChange: true })
const searchQuery = useRouteQuery<string>('sq', '')
const disableFilters = computed(() => !searchQuery.value)

const openFilter = defineModel('openFilter', { default: false })
const searchAuthor = defineModel<string>('searchAuthor', { default: '' })
const searchLegalCategory = defineModel<string>('searchLegalCategory', { default: '' })

const contentTypeOpen = ref<boolean>(false)
const legalCategoryOpen = ref<boolean>(false)
const authorOpen = ref<boolean>(false)
const periodOpen = ref<boolean>(false)
const sortOpen = ref<boolean>(false)

const FILTER_CATEGORIES: Array<{ label: string, open: Ref<boolean> }> = [
  { label: 'Innholdstype', open: contentTypeOpen },
  { label: 'Rettsområde', open: legalCategoryOpen },
  { label: 'Forfatter', open: authorOpen },
  { label: 'Periode', open: periodOpen },
  { label: 'Sortering', open: sortOpen },
]

const router = useRouter()

watch(
  () => router.currentRoute.value.path,
  () => {
    openFilter.value = false
  },
)

onScopeDispose(router.options.history.listen(() => {
  openFilter.value = false
  FILTER_CATEGORIES.forEach(({ open }) => (open.value = false))
}))
</script>

<template>
  <KsDrawer :open="openFilter" size="100%" position="bottom" :dismissable="false" demo="Layered" @close="openFilter = false">
    <template #header>
      <div class="flex justify-center w-full">
        <span class="text-small font-bold">Filter</span>
      </div>
    </template>
    <template #body>
      <div class="flex flex-col gap-4xl">
        <ul>
          <li v-for="(category, index) in FILTER_CATEGORIES" :key="index" class="border-b border-border-secondary">
            <button class="p-s flex items-center w-full justify-between text-primary" @click="category.open.value = true">
              {{ category.label }}
              <Icon :icon="faArrowRight" />
            </button>
          </li>
        </ul>
        <Button variant="primary" class="w-full" @click="openFilter = false">
          Vis {{ originalTotalResults }} treff
        </Button>
      </div>
      <Teleport to="body">
        <KsDrawer
          :open="contentTypeOpen"
          :backdrop="false"
          position="bottom"
          :dismissable="false"
          @close="contentTypeOpen = false"
        >
          <template #body>
            <DrawerBackButton @click="contentTypeOpen = false">
              Filter søket
            </DrawerBackButton>
            <fieldset class="w-full flex flex-col gap-xs pb-s px-s">
              <legend class="text-small font-bold mb-s">
                Innholdstype
              </legend>
              <SearchContentTypeFilter
                class="gap-s"
                :content-type-options="contentTypeOptions"
                :selected-content-types="filterState.selectedContentTypes.value"
                :toggle-content-type="filterState.toggleContentType"
                :disabled="disableFilters"
              />
            </fieldset>
            <SearchApplyFilterButton class="w-full" @click="contentTypeOpen = false" />
          </template>
        </KsDrawer>

        <KsDrawer
          :open="legalCategoryOpen"
          :backdrop="false"
          position="bottom"
          :dismissable="false"
          @close="legalCategoryOpen = false"
        >
          <template #body>
            <DrawerBackButton @click="legalCategoryOpen = false">
              Filter søket
            </DrawerBackButton>
            <div class="w-full flex flex-col gap-s pb-s px-s">
              <span class="text-small font-bold">
                Rettsområde
              </span>
              <FilterFacetList
                v-model:search="searchLegalCategory"
                class="gap-m"
                is-mobile
                label="Rettsområde"
                name="legalCategory-mobile"
                expand-noun="rettsområder"
                :options="legalCategoryOptions"
                :selected="filterState.selectedLegalCategories.value"
                :toggle="filterState.toggleLegalCategory"
              />
            </div>
            <SearchApplyFilterButton class="w-full" @click="legalCategoryOpen = false" />
          </template>
        </KsDrawer>

        <KsDrawer
          :open="authorOpen"
          :backdrop="false"
          position="bottom"
          :dismissable="false"
          @close="authorOpen = false"
        >
          <template #body>
            <DrawerBackButton @click="authorOpen = false">
              Filter søket
            </DrawerBackButton>
            <div class="w-full flex flex-col gap-s pb-s px-s">
              <span class="text-small font-bold">
                Forfattere
              </span>
              <FilterFacetList
                v-model:search="searchAuthor"
                class="gap-m"
                is-mobile
                label="Forfattere"
                name="author-mobile"
                expand-noun="forfattere"
                :options="authorOptions"
                :selected="filterState.selectedAuthors.value"
                :toggle="filterState.toggleAuthor"
              />
            </div>
            <SearchApplyFilterButton class="w-full" @click="authorOpen = false" />
          </template>
        </KsDrawer>

        <KsDrawer
          :open="periodOpen"
          :backdrop="false"
          position="bottom"
          :dismissable="false"
          @close="periodOpen = false"
        >
          <template #body>
            <DrawerBackButton @click="periodOpen = false">
              Filter søket
            </DrawerBackButton>
            <SearchPeriodFilter
              v-model:start-year="filterState.startYear.value"
              v-model:end-year="filterState.endYear.value"
              class="px-s"
              :disabled="disableFilters"
              @update="filterState.updateYearFilter"
            />
            <SearchApplyFilterButton class="w-full" @click="periodOpen = false" />
          </template>
        </KsDrawer>

        <KsDrawer
          :open="sortOpen"
          :backdrop="false"
          position="bottom"
          :dismissable="false"
          @close="sortOpen = false"
        >
          <template #body>
            <DrawerBackButton @click="sortOpen = false">
              Filter søket
            </DrawerBackButton>
            <div class="w-full flex flex-col gap-s pb-s px-s">
              <span class="text-small font-bold">
                Sorter på
              </span>
              <SearchSortingRadioGroup
                :sort-order="filterState.sortOrder.value"
                @update:sort-order="changeSortOrder"
              />
            </div>
            <SearchApplyFilterButton class="w-full" @click="sortOpen = false" />
          </template>
        </KsDrawer>
      </Teleport>
    </template>
  </KsDrawer>
</template>

<style scoped lang='postcss'>
:deep(.ks-dialog) {
  @apply min-h-[60vh];
}

:deep(.ks-dialog-body) {
  @apply h-full pb-m px-s pt-xs flex flex-col gap-m;
}

:deep(.ks-dialog-header) {
  @apply px-s pt-xs;
}
</style>
